import { HttpClientModule, HttpClientXsrfModule, HttpClientJsonpModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';

import { HsgAscModule, HsgModalConfig, HSG_MODAL_DEFAULT_OPTIONS, HsgModalPosition } from '@hhsg/hsg-asc';

import { AdminModule } from './smt/admin/admin.module';
import { AppRoutingModule } from './app-routing.module';
import { AppSettings } from '../shared/models/app-settings.model';
import { AppComponent } from './app.component';
import { AuthInterceptor } from '../shared/services/authorized.interceptor';
import { ConfigService } from '../shared/services/config.service';
import { DashboardComponent } from './dashboard/dashboard.component';
import { environment } from 'src/environments/environment';
import { KeysPipe } from 'src/shared/pipes/keys';
import { LoginComponent } from './login/login.component';
import { PermissionsService } from '../shared/services/permissions.service';
import { ReportsModule } from './smt/reports/reports.module';
import { SmtSharedModule } from '../shared/module/shared.module';
import { StaffingModule } from './smt/staffing/staffing.module';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { WorkforceRouteGuardService } from './workforce-route-guard.service';
import { CostCenterSelectComponent } from './workflow/cost-center-select/cost-center-select.component';
import { ShiftBlockSelectComponent } from './workflow/shift-block-select/shift-block-select.component';
import { InpatientReviewPreviousComponent } from './workflow/inpatient/review-previous/review-previous.component';
import { InpatientEnterCurrentComponent } from './workflow/inpatient/enter-current/enter-current.component';
import { EnterReasonsComponent } from './workflow/enter-reasons/enter-reasons.component';
import { WorkflowHeaderComponent } from './workflow/workflow-header/workflow-header.component';
import { WorkflowComponent } from './workflow/workflow.component';
import { MissingBlocksModalComponent } from './workflow/missing-blocks-modal/missing-blocks-modal.component';
import { NoStaffingGridModalComponent } from './workflow/no-staffing-grid-modal/no-staffing-grid-modal.component';
import { PageComponent } from './workflow/planner/page/page.component';
import { PageHeaderComponent } from './workflow/planner/page/page-header/page-header.component';
import { PlannerComponent } from './workflow/planner/planner.component';
import { PageDirective } from './workflow/planner/page/page.directive';
import { PageFooterComponent } from './workflow/planner/page/page-footer/page-footer.component';
import { ReviewPreviousRoomsComponent } from './workflow/acuity-rooms/review-previous-rooms/review-previous-rooms.component';
import { EnterCurrentRoomsComponent } from './workflow/acuity-rooms/enter-current-rooms/enter-current-rooms.component';
import { AcuityReviewPreviousComponent } from './workflow/acuity/review-previous/review-previous.component';
import { AcuityEnterCurrentComponent } from './workflow/acuity/enter-current/enter-current.component';
import { EnterUOSComponent } from './workflow/enter-uos/enter-uos.component';
import { TotalUOSEnterCurrentComponent } from './workflow/total-uos/enter-current/enter-current-uos.component';
import { TotalUOSReviewPreviousByBlockComponent } from './workflow/total-uos/review-previous/review-previous-uos.component';
import { WorkflowFinishComponent } from './workflow/planner/workflow-finish.component';
import { GoogleAnalyticsService } from '../shared/services/google-analytics.service';
import { GoogleAnalyticsEventTrackerDirective } from '../shared/directives/google-analytics-event-tracker.directive';
import { HoursCalculatorComponent } from './workflow/hours-calculator/hours-calculator.component';
import { CurrentBlockStaffingGridModalComponent } from './workflow/inpatient/current-block-staffing-grid-modal/current-block-staffing-grid-modal.component';
import { AnotherUserModalComponent } from './workflow/another-user-modal/another-user-modal.component';
import { TotalUosEnterActualComponent } from './workflow/total-uos/enter-actual/enter-actual-uos.component';
import { EnterActualUosComponent } from './workflow/total-uos/enter-actual-block/enter-actual-uos-block.component';
import { TotalUosEnterReasonComponent } from './workflow/total-uos/enter-reason/enter-reason-uos.component';
import { EnterReasonBlockComponent } from './workflow/total-uos/enter-reason-block/enter-reason-uos-block.component';
import { ImportTotalUosModalComponent } from './workflow/import-total-uos-modal/import-total-uos-modal.component';
import { ImportTimeAndAttendanceModalComponent } from './workflow/import-time-and-attendance-modal/import-time-and-attendance-modal.component';

export function loadConfigService(config: ConfigService): () => Promise<AppSettings> {
	return () => config.load();
}

@NgModule({
    declarations: [
        AppComponent,
        CostCenterSelectComponent,
        DashboardComponent,
        InpatientEnterCurrentComponent,
        EnterReasonsComponent,
        KeysPipe,
        LoginComponent,
        InpatientReviewPreviousComponent,
        ShiftBlockSelectComponent,
        UnauthorizedComponent,
        WorkflowHeaderComponent,
        WorkflowComponent,
        MissingBlocksModalComponent,
        NoStaffingGridModalComponent,
        PageComponent,
        PageHeaderComponent,
        PlannerComponent,
        PageDirective,
        PageFooterComponent,
        ReviewPreviousRoomsComponent,
        EnterCurrentRoomsComponent,
        AcuityReviewPreviousComponent,
        AcuityEnterCurrentComponent,
        EnterUOSComponent,
        TotalUOSEnterCurrentComponent,
        TotalUOSReviewPreviousByBlockComponent,
        WorkflowFinishComponent,
        GoogleAnalyticsEventTrackerDirective,
        HoursCalculatorComponent,
        CurrentBlockStaffingGridModalComponent,
        AnotherUserModalComponent,
        TotalUosEnterActualComponent,
        EnterActualUosComponent,
        TotalUosEnterReasonComponent,
        EnterReasonBlockComponent,
        ImportTotalUosModalComponent,
        ImportTimeAndAttendanceModalComponent
    ],
    imports: [
        AdminModule,
        AppRoutingModule,
        BrowserModule,
        FormsModule,
        HttpClientModule,
        HttpClientJsonpModule,
        HttpClientXsrfModule.withOptions({
            cookieName: 'XSRF-TOKEN',
            headerName: 'X-XSRF-TOKEN'
        }),
        ReactiveFormsModule,
        ReportsModule,
        SmtSharedModule,
        StaffingModule,
        HsgAscModule.forRoot('assets/app-config.json', environment as any)
    ],
    providers: [
        ConfigService,
        PermissionsService,
        WorkforceRouteGuardService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: loadConfigService,
            deps: [ConfigService],
            multi: true
        },
        {
            provide: HSG_MODAL_DEFAULT_OPTIONS,
            useValue: <HsgModalConfig>{
                hasBackdrop: true,
                backdropClass: 'overlay',
                minWidth: '350px',
                minHeight: '200px',
                position: <HsgModalPosition>{ top: '80px' }
            }
        },
        GoogleAnalyticsService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
